import React, { Dispatch, useContext, useEffect, useState } from 'react';
import { FormControl, Input, InputLabel, Button, Box, Typography } from '@mui/material';
import {
  confirmUserAttribute,
  fetchUserAttributes,
  getCurrentUser,
  sendUserAttributeVerificationCode,
  signOut,
} from 'aws-amplify/auth';
import { DICTIONARY, SHOW_MODAL } from 'shared/constants';
import { clearEmailVerificationStage } from 'shared/utilities';
import UserContext, { APP_AUTH_STATES } from 'shared/contexts/UserContext';
import AuthService from 'services/auth';
import NationalService from 'services/national';
import PersonService from 'services/person';
import { Role } from 'API';

interface Props {
  dispatch: Dispatch<LoginAction>;
  state: LoginState;
}

const VerifyEmail: React.FC<Props> = ({ dispatch, state }: Props) => {
  const [, userDispatch] = useContext(UserContext);
  const [codeSent, setCodeSent] = useState(false);

  const { verificationError, verificationCode } = state;

  const completeLogin = async () => {
    clearEmailVerificationStage();

    const currentUser = await getCurrentUser();
    const person = await PersonService.getCurrent();
    const role = await AuthService.getRoleForCurrentUser();
    const {
      accessCompanies, adminCompanies,
    } = await AuthService.getCompaniesCurrentUserCanAccess();
    const dependents = role === Role.FOREIGNNATIONAL
      ? NationalService.getDependentIDs(person) : [];

    if (person) {
      PersonService.setPersonID(person.id);
      PersonService.setPersonType(person.__typename);
      await PersonService.updateHasLoggedIn(person);
      localStorage.setItem(SHOW_MODAL, 'true');
    }

    userDispatch({
      type: 'login',
      authState: APP_AUTH_STATES.SignedIn,
      authData: currentUser,
      person,
      role,
      accessCompanies,
      adminCompanies,
      dependents,
    });
  };

  // The persisted stage brings the user straight back here on every load, so
  // resolve it against Cognito first: the address may already be verified (MH
  // changed it staff-side), or there may be no session left to verify with.
  // Either way the code below is the only thing that can release the screen.
  useEffect(() => {
    const resolveStaleStage = async () => {
      let attributes;

      try {
        attributes = await fetchUserAttributes();
      } catch {
        // Nothing left to verify against - the stage outlived the session, and
        // the code this screen wants can no longer be sent or confirmed.
        clearEmailVerificationStage();
        dispatch({ type: 'cancel email verification' });

        return;
      }

      if (attributes.email_verified !== 'true') {
        return;
      }

      try {
        await completeLogin();
      } catch (error: any) {
        // The address is verified; only the profile lookups failed. Keep the
        // session and report it rather than dropping the user at sign-in.
        dispatch({ type: 'verification error', error: error.message });
      }
    };

    resolveStaleStage();
  }, []);

  const handleChallengeSubmit = async () => {
    dispatch({ type: 'loading' });

    try {
      await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: verificationCode });

      await completeLogin();
    } catch (error: any) {
      dispatch({ type: 'verification error', error: error.message });
    }
  };

  const handleSendCode = async () => {
    setCodeSent(true);
    await sendUserAttributeVerificationCode({ userAttributeKey: 'email' });
  };

  // Without this the screen has no exit when the code never arrives, because
  // the stage outlives the session in localStorage.
  const handleCancel = async () => {
    clearEmailVerificationStage();

    try {
      await signOut();
    } catch (error) {
      // The stage is already cleared, so return to the form either way rather
      // than leaving an unhandled rejection and a dead screen.
      // eslint-disable-next-line no-console
      console.warn(error);
    }

    dispatch({ type: 'cancel email verification' });
  };

  return (
    <>
      <Typography sx={{ marginBottom: '2rem' }}>
        {DICTIONARY.VerifyEmailPrompt}
      </Typography>

      {verificationError && (
        <Typography className="error-text">{verificationError}</Typography>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleChallengeSubmit();
        }}
      >
        <FormControl sx={{ marginBottom: '1rem', width: '100%' }}>
          <InputLabel htmlFor="verify-input">
            {DICTIONARY.VerificationCode}
          </InputLabel>
          <Input
            id="verify-input"
            value={verificationCode}
            onChange={(e) => dispatch({
              type: 'field',
              fieldName: 'verificationCode',
              payload: e.currentTarget.value,
            })}
            type="text"
          />
        </FormControl>

        <Box display="flex" flexDirection="column">
          <Box display="flex">
            <button
              className="button button--small"
              onClick={handleCancel}
              type="button"
            >
              {DICTIONARY.CancelReset}
            </button>
            <Button
              sx={{ marginLeft: 'auto' }}
              onClick={handleChallengeSubmit}
              color="primary"
              variant="contained"
            >
              {DICTIONARY.Verify}
            </Button>
          </Box>
          <Typography
            sx={(t) => ({
              color: t.palette.grey4,
              fontSize: '0.875rem',
              marginLeft: 'auto',
              marginTop: '1rem',
              '& .button': { color: t.palette.grey4 },
            })}
          >
            {codeSent ? (
              DICTIONARY.VerifyCodeSent
            ) : (
              <button
                onClick={handleSendCode}
                className="button button--inline"
                type="button"
              >
                {DICTIONARY.RequestVerificationCode}
              </button>
            )}
          </Typography>
        </Box>
      </form>
    </>
  );
};

export default VerifyEmail;
